import { reactive, toRefs, onBeforeMount, onBeforeUnmount, onMounted, ref, computed,Ref, nextTick, watch,getCurrentInstance  } from 'vue'
import { useStore } from 'vuex'
import useReceiveMessage, { getCanvasLimitHeight } from '@/hooks/useReceiveMessage'
import useLoading3D from '@/hooks/useLoading3D'
import useSpeechSynthesis from '@/hooks/useSpeechSynthesis'
import { $enum, $config, $tips } from '@/global'
import { SystemMode } from '@/types/system'
import {
    enterOrExitUavSimulateModule,
    resetView,
    switchUnlockUav,
    forceGainFocus,
    recordProcessMessage
} from '@/webapi'
import CircleDrawer from '@/utils/drawer/CircleDrawer'
import { IPoint } from '@/utils/drawer/BaseDrawer'
import { createAttemptRecorder } from './attemptRecorder'
import {createRandomWind,getWindDirection} from '@/utils/index'
import useFlightEchartOptions from '@/hooks/useFlightEchartOptions'
import { createTraining} from '@/service'

// 成绩页没有无人机图标，但轨迹和偏差点仍需保留回放页相同的边界留白。
class ExamResultDrawer extends CircleDrawer {
    protected getBoundedCanvasPoint(pos: IPoint, halfWidth = 0, halfHeight = 0): IPoint {
        // BaseDrawer.drawCompass 中图标边长为 viewportWidth * 24 / 400。
        const inset = this.viewportWidth * (12 / 400)
        return super.getBoundedCanvasPoint(
            pos,
            Math.min(this.viewportWidth / 2, Math.max(halfWidth, inset)),
            Math.min(this.viewportHeight / 2, Math.max(halfHeight, inset)),
        )
    }
}

interface IFlyingEmit {
    (event: 'onBack'): void
}

enum PageName {
    加载中=1,
    飞行=2,
    成绩=3
}

enum MassageType {
    无=0,
    自转=1,
    八字=2,
    降落=3,
    解锁=4,
}

enum ExceedType {
    无 = 0,
    偏高=1,
    偏位=2,
    偏度=3,
    偏角=4,
    高度=5,
}

//报警类型
enum AlarmType {
    操作=1,
    异常=2
}

const colorMap: Record<string, string> = {
    'height': '#6BA4FF',
    'position': '#D57D0F',
    'timeDeviation': '#46B65B',
    'headAngle': '#FDE043',
    'angle': '#D279D3',
    'speed': '#38B6C7'
}
const MAX_RETRY_COUNT = 2
export default function useFlying(
    emit: IFlyingEmit,
    timeBox:any,
    progressRef:any,
    flyCanvasRef:any,
    gradCanvasRef:any,
    flyDeviationInfoRef:any,
    gradeFlyDeviationInfoRef:any,
): any {
    const store = useStore()
    const { proxy } = getCurrentInstance() as any
    const typeMap = new Map([
        ['notifyRunningParam', 'notifyRunningParam'], //运行数据回调
        ['loadResourceFail', 'loadResourceFail'], //加载模型失败
        ['loadSceneResource', 'loadSceneResource'], //加载模型完成
        ['modelLoading', 'modelLoading'], //加载模型进度
        ['initializeEngineProgress', 'initializeEngineProgress'],//引擎初始化
        ['extraLoadModelFiles','extraLoadModelFiles'], //额外的模型文件列表
        ['exitSimulateModuleSuccess','exitSimulateModuleSuccess'], //退出模拟模块成功
        ['rotatePartOneComplete','rotatePartOneComplete'], //旋转第一部分完成

    ])
    const state = reactive({
        sceneImage: $config.FILE_ROOT_PATH + store.state.persistence.currentUavInfo.sceneImageUrl,
        uavId: store.state.persistence.currentUavInfo.uavId,
        nestId: store.state.persistence.currentUavInfo.nestId,
        trainingId: store.state.persistence.currentUavInfo.trainingId,
        stationdId: store.state.persistence.stationId,
        sceneInfo:store.state.persistence.currentUavInfo.sceneInfo,
        resourceFileList:store.state.persistence.currentUavInfo?.resourceFileList[0] || [],
        windDirection:null as any,
        connectStatus: $enum.SWITCH_STATUS.OFF, // 遥控器连接状态
        lockStatus:$enum.SWITCH_STATUS.OFF,
        propgressTimer: null as any, // 进度条计时器
        step: 1, // 进度条步长
        isComputing: false, // 是否计算中
        isShowGrad:false,
        pixiApp: store.state.viewData.pixiInstance, //pixi实例
        currentPage:PageName.加载中,
        gradInfo:{} as any,
        finish:false,//是否考试完成
        gradScore:0,//得分
        evaluate:false,
        drawer:null as any, // 圆形绘制器实例
        gradDrawer:null as any, // 圆形绘制器实例
        isShowMessage:false, //是否显示消息
        chanceCount:3,//消息显示次数
        totalTime:0,//总时间
        gradeTotalTime:0,//成绩总时间
        pageVisible:true, //页面是否显示
        engineProgress:0,
        loading3D:{
            title:store.state.persistence.currentUavInfo.sceneInfo?.sceneSubTypeName,
            description:store.state.persistence.currentUavInfo.sceneInfo?.typeDescription,
            pageType:store.state.persistence.currentUavInfo.parentName,
        },
        fpsStatus:60,
        canSetFps:true,
        collisionAlarm:false,
        crash:false,
        currentWindSpeed:'0.0',
        aiCoachSettlementData:null as null | {
            rating: '较差' | '良好' | '优秀',
            radar: {
                flight_stability: number,
                stick_proficiency: number,
                anti_disturbance: number,
                error_correction: number,
                deviation_control: number
            },
            flightConclusion: string,
            trainingSuggestion: string
        },
        flyDeviationViewMode:$enum.FlyDeviationViewMode.卡片,
        circleDatas: [],
        flyingInfo:{} as any, // 飞行过程中的数据，用于收集曲线数据
        permissionLevel:
            store.state.viewData.systemMode === SystemMode.PILOT_ASSESSMENT
                ? 1
                : store.state.persistence.breadcrumb[0]?.permissionLevel,
        currentInfoMessage:null as string | null | undefined,
        currentErrorMessage:null as string | null | undefined,
        currentErrorAlarmCode:null as string | null | undefined,
        backByExitSimulateModule:false,
        retryPhase:'running' as 'running' | 'returning' | 'finished',
        retryCount:MAX_RETRY_COUNT,
        attemptNo:1,
        failedItemName:'',
    })
    const operationPrompt = reactive({
        spinCompleted:false,//自旋完成
        delayTime:3000,
    })
    let isUnmounted = false
    const pendingPromptTimers = new Set<ReturnType<typeof setTimeout>>()

    function schedulePrompt(callback: () => void, delay: number) {
        const timer = setTimeout(() => {
            pendingPromptTimers.delete(timer)
            if(!isUnmounted){
                callback()
            }
        }, delay)
        pendingPromptTimers.add(timer)
    }

    function clearPendingPrompts() {
        pendingPromptTimers.forEach((timer) => clearTimeout(timer))
        pendingPromptTimers.clear()
    }

    const flyingState = reactive({
        evaluateStatus:{
            height:false,
            position:false,
            time:0,
            angle:false,
            angleMaxSpeed:false,
            angleMinSpeed:false,
            calculateAngleSpeed:false, //到达中心桶并且角速度第一次超过最小值时改为true
            maxSpeed:false,
            minSpeed:false,
            calculateMinSpeed:false, //到达中心桶并且最小速度第一次超过最小值时改为true
            collisionAlarm:false
        },
        totalSeconds: -1, // 飞行总时间 秒
        currentSeconds: 0, // 当前时间 秒
        uavStatusList: [],
        currentStepIndex:0,
        isShowAlarm:false,
        currentMessage:MassageType.无,
        isExceed:false, //是否超出
        exceedType:ExceedType.无, //超出类型
        onGround:false,
        pointList:[] as any, // 点
        arriveCenter:0,//是否到达中心
        isReady:false,
    });

    const { speakState, speak, stop} = useSpeechSynthesis()
    const { echart, initEcharts, resizeEcharts, updateRadarData } = useFlightEchartOptions()

    const updateFlyingInfo = (data:any) => {
        const max = Number(data?.maxValue || 0);
        const min = Number(data?.minValue || 0);
        if(data?.value>max&&data?.value<max+0.01){
            data.value = max+0.01
        }
        let value = Number(data?.value || 0);
        let realTimeValue = Number(data?.realTimeValue || 0);
        let lowerValue:any = Number(data?.lowerValue || 0);
        let higherValue:any = Number(data?.higherValue || 0);
        let range = max - min;
        let leftValue = Math.max(min, lowerValue);
        let rightValue = Math.min(max, higherValue);
        let progress = Math.min(1, (rightValue - leftValue) / range) * 100;
        let color = lowerValue < min || higherValue > max ? '#E70102' : '#2174FF ';
        let left = (leftValue - min) / range * 100;
        return {value,progress,max,min,name:data?.name, lowerValue, higherValue,color,left,realTimeValue,id:data?.id};
    }

    const updateFlightMetric = (data:any, metric:any,unit:string) => {
        if(!data) return;
        if(Array.isArray(data)){
            if(data.length){
                let arr:any[] = []
                data.forEach(item=>{
                    let {value,max,min,name, lowerValue, higherValue,realTimeValue,id} = updateFlyingInfo(item);
                    arr.push({
                        name,
                        id,
                        value:value.toFixed(0),
                        realTimeValue:realTimeValue.toFixed(0),
                        max,
                        min,
                        lowerValue:lowerValue.toFixed(2),
                        higherValue:higherValue.toFixed(2),
                        progress: 0,
                        color:'',
                        left:0,
                        unit
                    });
                })
                if(state.currentPage == PageName.飞行){
                    state.flyingInfo[metric] = arr
                }else if(state.currentPage == PageName.成绩){
                    state.gradInfo[metric] = arr
                }
            }else{
                let name = metric == 'timeDeviation'?'用时偏差':'';
                if(state.currentPage == PageName.飞行){
                    state.flyingInfo[metric] = [{
                        name,
                        id:state.flyingInfo[metric][0]?.id,
                        value:0,
                        realTimeValue:0,
                        max:0,
                        min:0,
                        lowerValue:0,
                        higherValue:0,
                        progress: 0,
                        color:'',
                        left:0,
                        unit
                    }]
                }else if(state.currentPage == PageName.成绩){
                    state.gradInfo[metric] = [{
                        name,
                        id:state.gradInfo[metric][0]?.id,
                        value:0,
                        realTimeValue:0,
                        max:0,
                        min:0,
                        lowerValue:0,
                        higherValue:0,
                        progress: 0,
                        color:'',
                        left:0,
                        unit
                    }]
                }
            }
        }else{
            let {value,progress,max,min,name, lowerValue, higherValue,color,left,realTimeValue,id} = updateFlyingInfo(data);
            if(metric == 'headAngle'){
                //夹角偏差
                lowerValue = lowerValue.toFixed(1)
                higherValue = higherValue.toFixed(1)
            }else{
                lowerValue = lowerValue.toFixed(2)
                higherValue = higherValue.toFixed(2)
            }
            if(state.currentPage == PageName.飞行){
                state.flyingInfo[metric] = {
                    name,
                    id,
                    value,
                    realTimeValue,
                    max,
                    min,
                    lowerValue,
                    higherValue,
                    progress,
                    color,
                    left,
                    unit
                };
            }else if(state.currentPage == PageName.成绩){
                state.gradInfo[metric] = {
                    name,
                    id,
                    value,
                    realTimeValue,
                    max,
                    min,
                    lowerValue,
                    higherValue,
                    progress,
                    color,
                    left,
                    unit
                };
            }
        }
    }

    const controlExceedMessage = (message:string,alarmCode?:string | null | undefined)=>{
        // proxy?.vknMessage.close();
        flyingState.isShowAlarm = true;
        showExceedMessage(message,alarmCode)
    }

    const loadingProgess = computed(() => {
        if(engineInitialized.value){
            return loadingState.progressValue;
        }else{
            return (state.engineProgress * 100).toFixed(2);
        }
    });

    const isOnlineSystem = computed(() => store.state.viewData.systemMode === SystemMode.ONLINE_TRAINING )


     ///////////////////////////////////////////////////////  事件

     const { loadingState, setLoadingStatus, engineInitialized, handleOpenPDF, storeSettlementData,setExtraFileMap, showOrHideEngineView } = useLoading3D(state.stationdId, state.resourceFileList, proxy)
     let pointList:any[] = []
     let retryCheckpointKey = ''
     let retryHasLeftCenter = false
     const attemptRecorder = createAttemptRecorder(colorMap)
     let retryCheckpoint:{
        pointList:any[];
        circleDatas:any[];
        flyingInfo:any;
        chartData:any;
     } | null = null
     let testRetryCount = -1;
     const { handleOpenSettingFullscreen, handleBack } = useReceiveMessage(typeMap, (type: string, data: any) => {
        if (type === 'notifyRunningParam' &&  state.currentPage !== PageName.成绩) {
            const {taskData, joyStickMode, unLockStatu,onGround,notifyKeyList,isReady,yawAngleSpeed,horizonalSpeed,fps,currentWindSpeed} = data.data || {}
            const { stepNameList, currentStepIndex, evaluateData,finish,targetTime, currentTime,score,isPass,overLimit, totalTime,mapPosition,mapYaw, onGroundAlarm, arriveCenter, collisionAlarm, crash, aiCoachSettlementData,triggerRetry, retryCount } = taskData || {}
            // console.log('notifyRunningParam-----',data.data)
            if(retryCount != testRetryCount){
                testRetryCount = retryCount
                console.log('notifyRunningParam-----',testRetryCount)
            }
            Object.assign(state,{
                lockStatus:unLockStatu,
                connectStatus:joyStickMode,
                totalTime:totalTime,
                collisionAlarm,
                crash,
                currentWindSpeed:currentWindSpeed?.toFixed(1) || '0.0',
            })
            if(state.canSetFps){
                state.fpsStatus = fps;
            }
            if(retryCount !== undefined && Number.isFinite(Number(retryCount))){
                state.retryCount = Math.max(0, Number(retryCount))
            }
            if (notifyKeyList && notifyKeyList.length > 0)
            {
                notifyKeyList.forEach((item: string) => {
                    if(item == "Escape")
                    {
                        handleBack('Escape');
                    }else if(item == "F11"){
                        window.listenEvent.emit('toggle-full-screen');
                    }
                });
            }
            // 必须在原重试逻辑恢复检查点之前备份；异常标志可早于 finish 到达。
            attemptRecorder.record(taskData || {}, () => ({
                pointList: retryCheckpoint?.pointList || pointList,
                circleDatas: retryCheckpoint?.circleDatas || state.circleDatas,
                chartData: retryCheckpoint?.chartData || flyDeviationInfoRef?.value?.getChartData?.() || null,
            }))
            if(!finish && !attemptRecorder.isForced()){
                const isRetryTriggered = triggerRetry === true
                const hasArrivedCenter = Boolean(arriveCenter)
                if(isRetryTriggered){
                    handleRetryTrigger(retryCount, evaluateData, hasArrivedCenter)
                }
                // triggerRetry 是一次性字段，retryPhase 用于保留“已触发重试”的状态。
                // 只有已触发重试并重新到达中心桶后，才恢复检查点数据。
                if(!isRetryTriggered && state.retryPhase == 'returning' && !hasArrivedCenter){
                    retryHasLeftCenter = true
                }
                const isRetryResume = !isRetryTriggered && state.retryPhase == 'returning' && retryHasLeftCenter && hasArrivedCenter
                if(isRetryResume){
                    handleRetryResume()
                }
                const canProcessFlight = !isRetryTriggered && state.retryPhase == 'running'
                if(state.finish){
                    state.finish = false;
                }
                if(canProcessFlight && (currentStepIndex > 0 || !hasFlowStep(stepNameList, '解锁')) && state.isShowMessage && overLimit == 0){
                    state.isShowMessage = false;
                }
                if(canProcessFlight && !flyingState.arriveCenter && arriveCenter){
                    flyingState.arriveCenter = arriveCenter;
                }
                if(canProcessFlight){
                    normalPrompt({currentStepIndex,overLimit,stepNameList,arriveCenter,targetTime,isReady})
                }
                 // 一次性更新飞行状态
                const prevIsReady = flyingState.isReady;
                Object.assign(flyingState, {
                    uavStatusList: stepNameList,
                    currentStepIndex,
                    totalSeconds:targetTime,
                    currentSeconds:currentTime?.toFixed(0),
                    onGround:onGround,
                    isReady:isReady,
                })
                // 广播 isReady 状态变化给布局组件（仅在状态变化时广播）
                if (isReady && isReady !== prevIsReady) {
                    window.listenEvent.emit('is-ready-change', isReady);
                }
                if(canProcessFlight && state.permissionLevel == 1 && evaluateData && typeof evaluateData == "object"){
                    const {
                        heightOffset,
                        positionOffset,
                        angleOffset,
                        angleSpeedOffset,
                        speedOffset,
                        time
                    } = evaluateData;
                    const isFirstInit = Object.keys(state.flyingInfo).length === 0;
                    const shouldUpdate = isFirstInit || (flyingState.arriveCenter && flyingState.currentMessage !== MassageType.降落);
                    if (shouldUpdate) {
                        if (heightOffset !== undefined) updateFlightMetric(heightOffset, 'height', 'm');
                        if (positionOffset !== undefined) updateFlightMetric(positionOffset, 'position', 'm');
                        if (time !== undefined) updateFlightMetric(time, 'timeDeviation', 's');
                        if (angleOffset !== undefined) updateFlightMetric(angleOffset, 'headAngle', '°');
                        if (angleSpeedOffset !== undefined) updateFlightMetric(angleSpeedOffset, 'angle', '°/s');
                        if (speedOffset !== undefined) updateFlightMetric(speedOffset, 'speed', 'm/s');
                        // 更新曲线数据
                        flyDeviationInfoRef?.value?.updateCurveData?.()
                    }
                }


                if(canProcessFlight && (currentStepIndex > 0 || !hasFlowStep(stepNameList, '解锁'))){
                    abnormalPrompt({evaluateData,yawAngleSpeed,horizonalSpeed,collisionAlarm,mapPosition,mapYaw})
                }
                
                if(canProcessFlight && mapPosition && (currentStepIndex > 0 || !hasFlowStep(stepNameList, '解锁'))){
                    pointList.push({
                        x:mapPosition.x,
                        y:mapPosition.y,
                        yaw:mapYaw,
                    })
                }
                if(canProcessFlight && arriveCenter){
                    saveRetryCheckpoint(currentStepIndex)
                }
            }
            if(!finish){
                if((currentStepIndex > 0 || !hasFlowStep(stepNameList, '解锁'))){
                    if(overLimit == 1){
                        if(!flyingState.isShowAlarm){
                            proxy?.vknMessage.close();
                            controlExceedMessage('飞行高度过高，考核不通过。')
                        }
                    }

                    if(onGroundAlarm){
                        if(!flyingState.isShowAlarm){
                            proxy?.vknMessage.close();
                            controlExceedMessage('无人机着陆，即将自动结束本轮考试。')
                        }
                    }
                }
            }

            if(finish && !state.finish){
                proxy?.vknMessage.close();
                const forcedResult = attemptRecorder.getForcedResult()
                if(forcedResult){
                    pointList = forcedResult.pointList
                    state.circleDatas = forcedResult.circleDatas as any
                }
                let chartData:any = null
                if(state.permissionLevel == 1){
                    // 获取飞行过程中收集的曲线数据
                    chartData = forcedResult ? forcedResult.chartData : flyDeviationInfoRef?.value?.getChartData?.();
                }

                Object.assign(state,{
                    finish:true,
                    retryPhase:'finished',
                    gradScore:score != null && score != undefined?score.toFixed(0):0,//得分
                    evaluate:isPass,
                    gradeTotalTime:totalTime,//成绩总时间
                    aiCoachSettlementData: aiCoachSettlementData ?? null
                })
                if(!isPass){
                    state.chanceCount = Math.max(0, state.chanceCount - 1);
                }
                // 先切换页面状态，再调用 updateFlightMetric（依赖 currentPage 判断写入哪个数据源）
                flyingState.isShowAlarm = false;
                flyingState.exceedType = ExceedType.无;
                state.currentPage = PageName.成绩
                handleFinishMessage()
                if(state.permissionLevel == 1){
                    if(state.flyDeviationViewMode != $enum.FlyDeviationViewMode.曲线){
                        state.flyDeviationViewMode = $enum.FlyDeviationViewMode.曲线;
                    }
                }

                updateFlightMetric(evaluateData?.["heightOffset"], 'height','m');
                updateFlightMetric(evaluateData?.["positionOffset"], 'position','m');
                updateFlightMetric(evaluateData?.["time"], 'timeDeviation','s');
                updateFlightMetric(evaluateData?.["angleOffset"], 'headAngle','°');
                updateFlightMetric(evaluateData?.["angleSpeedOffset"], 'angle','°/s');
                updateFlightMetric(evaluateData?.["speedOffset"], 'speed','m/s');

                setTimeout(() => {
                    if(!state.gradDrawer && evaluateData?.["positionOffset"]){
                        const w = evaluateData?.["positionOffset"].maxValue * 10 * 2;
                        state.gradDrawer = new ExamResultDrawer(gradCanvasRef.value,w)
                        state.gradDrawer.setTrailPoints(pointList)
                        state.circleDatas.forEach(data=>{
                            // @ts-ignore
                            state.gradDrawer.drawCircle(data.pos,data.color);
                        })
                    }
                    if(state.gradDrawer){
                        state.gradDrawer.toGradPage(gradCanvasRef.value)
                        pointList = [];
                    }
                    if(state.permissionLevel == 1){
                         initEcharts()
                        // 更新雷达图数据
                        if(state.aiCoachSettlementData?.radar){
                            updateRadarData(state.aiCoachSettlementData.radar)
                        }
                        // 将飞行过程中收集的曲线数据导入到结算页组件
                        if(chartData && gradeFlyDeviationInfoRef?.value){
                            gradeFlyDeviationInfoRef.value.importChartData(chartData)
                        }
                    }

                }, 50);
                setTimeout(() => {
                    let fileData = state.gradDrawer.exportToImage();
                    storeSettlementData({
                        evaluateData,
                        fileData,
                        trainingId:store.state.persistence.currentUavInfo.trainingId,
                    })
                    if(state.permissionLevel == 1){
                        gradeFlyDeviationInfoRef?.value?.uploadAllChartImages?.(state.trainingId);
                    }
                }, 100);

            }
        }

        if(type === 'rotatePartOneComplete' && state.retryPhase == 'running'){
            showMessage("请改变旋转方向", AlarmType.操作, 5000, 'yjzldy','',true)       
        }

        if(type === 'initializeEngineProgress'){
            state.engineProgress = data.data?.progress || 0
        }

        if (type === 'loadSceneResource') {
            // clearInterval(loadingState.timer)
            state.currentPage = PageName.飞行;
            state.trainingId = store.state.persistence.currentUavInfo.trainingId;
            state.aiCoachSettlementData = null;
            const examWindDirection =
                store.state.viewData.systemMode === SystemMode.PILOT_ASSESSMENT
                    ? store.state.viewData.examWindDirection
                    : store.state.persistence.examWindDirection
            state.windDirection = getWindDirection(examWindDirection)
            enterOrExitUavSimulateModule({
                enter:true,
                uavSpecificationId:state.uavId,
                nestId:state.nestId,
                userId:store.state.persistence.userInfo.userId,
                tenantId:store.state.persistence.currentTenantId,
                volume:store.state.persistence.sounds.droneSound,
                forbidAiCoach:state.permissionLevel == 1?0:1,
                canvasLimitHeight:getCanvasLimitHeight(store.state.persistence.resolution),
                retryCount:MAX_RETRY_COUNT,
                trainingData:{
                    recordMode:state.permissionLevel,
                    id:state.trainingId,
                    sceneInfo:state.sceneInfo,
                    windSpeedRange:createRandomWind().speed,
                    windDirectionIndex:examWindDirection,
                    forbidSaveResult:'',
                    specifiedJoystick:store.state.persistence.userTrainSettingInfo.joystickSpecification
                }
            })

        }

         if (type === 'modelLoading') {
             //加载中
             // const { progressData} = data.data || {}
             // loadingState.loadingStatus = progressData
             // initTimer();
             setLoadingStatus(data.data);

         }

         if(type ==='extraLoadModelFiles')
         {
             setExtraFileMap(data.data);
         }

         if(type === 'exitSimulateModuleSuccess'){
            if(state.backByExitSimulateModule){
                handleBack();
            }
         }
    })

    ///////////////////////////////////////////////////////  状态

    ///////////////////////////////////////////////////////  计算属性
    // 遥控器连接图标
    const connectIcon = computed(() => {
        return state.connectStatus? require("@assets/images/connect.png"): require("@assets/images/disconnect.png")
    })



    const currentTime = computed(() => {
        const progress = flyingState.currentSeconds / flyingState.totalSeconds;
        const remainTime = flyingState.totalSeconds - flyingState.currentSeconds;
        const progressWidth = progress * 100;
        let adjustedRight;

        if (timeBox.value) {
            const rect = timeBox.value.getBoundingClientRect();
            const parentRect = timeBox.value.parentElement.getBoundingClientRect();
            const boxWidth = rect.width;
            const parentWidth = parentRect.width;

            // 计算剩余空间
            const remainingSpace = parentWidth - boxWidth;
            adjustedRight = remainingSpace * progress;
            if(flyingState.currentSeconds > 0){
                timeBox.value.style.right = `${adjustedRight}px`;
            }else if(flyingState.currentSeconds == 0){
                timeBox.value.style.right = `0px`;
            }
        }
        if (progressRef.value) {
            if(flyingState.currentSeconds >= 0){
                progressRef.value.style.width = `${adjustedRight}px`;
            }else if(flyingState.currentSeconds == 0){
                progressRef.value.style.width = `0px`;
            }
        }
        const minutes = Math.floor(remainTime / 60);
        const seconds = (remainTime % 60).toFixed(0);
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    })

    const lockIcon = computed(() => {
        if(state.lockStatus ==  $enum.SWITCH_STATUS.ON){
            return require("@/assets/images/unlock.png");
          }
          return require("@/assets/images/lock.png");
    })

    const gradTotalTime = computed(() => {
        const totalSeconds = state.currentPage == PageName.成绩?state.gradeTotalTime:state.totalTime;
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = Math.floor(totalSeconds - minutes * 60); // 关键修改：计算真正的剩余秒数
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    });

    ///////////////////////////////////////////////////////  监听

    ///////////////////////////////////////////////////////  方法

    // 显示超出区域提示
    const showExceedMessage = (message: string, alarmCode?:string | null | undefined) => {
        showMessage(message, AlarmType.异常, 0, 'gjts', "exceed",true, alarmCode)
    }

    const showMessage = (message: string, alarmType:AlarmType, duration?:number, icon?:string, type?:'exceed'|'',useVoice:true | false = true, alarmCode?:string | null | undefined) => {
        if(isUnmounted) return
        state.isShowMessage = true;
        if(useVoice){
            speak(message)
        }
        if(type == "exceed"){
            flyingState.isExceed = true;
        }
        let durationTime = duration !== null && duration !== undefined?duration:3000

        if(alarmType == AlarmType.异常){
            if(state.currentErrorMessage !== message){
                if(state.currentErrorMessage){
                    recordProcessMessage({message: null, messageType: 'error', alarmCode:state.currentErrorAlarmCode});
                }
            }
            recordProcessMessage({message, messageType: 'error',messageDuration:durationTime, alarmCode});

            state.currentErrorMessage = message
            state.currentErrorAlarmCode = alarmCode;
        }

        if(alarmType == AlarmType.操作){
            if(state.currentInfoMessage !== message){
                if(state.currentInfoMessage){
                     recordProcessMessage({message: null, messageType: 'info'});
                }
            }
            recordProcessMessage({message, messageType: 'info',messageDuration:durationTime});

            state.currentInfoMessage = message
        }

        proxy?.vknMessage.warning({
            message: message,
            duration:durationTime,
            offset: 100,
            onClose: () => {
                state.isShowMessage = false;
                if(type == "exceed"){
                    flyingState.isExceed = false;
                }
            }
        }, icon)
    }

    const cloneData = <T>(data:T):T => JSON.parse(JSON.stringify(data))

    const resetEvaluateStatus = () => {
        Object.assign(flyingState.evaluateStatus,{
            height:false,
            position:false,
            time:0,
            angle:false,
            angleMaxSpeed:false,
            angleMinSpeed:false,
            calculateAngleSpeed:false,
            maxSpeed:false,
            minSpeed:false,
            calculateMinSpeed:false,
            collisionAlarm:false,
        })
        flyingState.isShowAlarm = false
        flyingState.isExceed = false
        flyingState.exceedType = ExceedType.无
    }
    const getRetryFailureInfo = (evaluateData:any) => {
        const getEvaluateItem = (code:string) => {
            const directItem = evaluateData?.[code]
            if(directItem) return directItem
            return Object.values(evaluateData || {}).find((item:any) => item?.code == code) as any
        }
        const createFailureInfo = (code:string, promptText:string) => {
            const evaluateItem = getEvaluateItem(code)
            return {
                code:evaluateItem?.code || code,
                name:promptText.replace(/过大$|过小$/, ''),
                promptText,
            }
        }

        const heightOffset = getEvaluateItem('heightOffset')
        if(heightOffset?.value > heightOffset?.maxValue){
            return createFailureInfo('heightOffset', '高度偏差过大')
        }
        const positionOffset = getEvaluateItem('positionOffset')
        if(positionOffset?.value > positionOffset?.maxValue){
            return createFailureInfo('positionOffset', '位置偏差过大')
        }
        const angleOffset = getEvaluateItem('angleOffset')
        if(angleOffset?.value > angleOffset?.maxValue){
            return createFailureInfo('angleOffset', '航向夹角过大')
        }
        const angleSpeedOffset = getEvaluateItem('angleSpeedOffset')
        if(angleSpeedOffset?.underMin){
            return createFailureInfo('angleSpeedOffset', '角速度过小')
        }
        if(angleSpeedOffset?.value > angleSpeedOffset?.maxValue){
            return createFailureInfo('angleSpeedOffset', '角速度过大')
        }
        const speedOffset = getEvaluateItem('speedOffset')
        if(speedOffset?.underMin){
            return createFailureInfo('speedOffset', '速度过小')
        }
        if(speedOffset?.value > speedOffset?.maxValue){
            return createFailureInfo('speedOffset', '速度过大')
        }
        return { code:'', name:'偏差', promptText:'偏差异常' }
    }

    const saveRetryCheckpoint = (currentStepIndex:number) => {
        const checkpointKey = `${currentStepIndex}:${state.attemptNo}`
        if(retryCheckpointKey == checkpointKey) return
        retryCheckpointKey = checkpointKey
        retryCheckpoint = {
            pointList:cloneData(pointList),
            circleDatas:cloneData(state.circleDatas),
            flyingInfo:cloneData(state.flyingInfo),
            chartData:flyDeviationInfoRef?.value?.getChartData?.() || null,
        }
    }

    const restoreRetryCheckpoint = () => {
        pointList = cloneData(retryCheckpoint?.pointList || [])
        state.circleDatas = cloneData(retryCheckpoint?.circleDatas || []) as any
        state.flyingInfo = cloneData(retryCheckpoint?.flyingInfo || {})
        flyDeviationInfoRef?.value?.resetCharts?.()
        if(retryCheckpoint?.chartData){
            flyDeviationInfoRef?.value?.importChartData?.(retryCheckpoint.chartData)
        }
    }

    const handleRetryTrigger = (remainingRetryCount:any, evaluateData:any, arriveCenter:boolean) => {
        const retryCount = Math.max(0, Number(remainingRetryCount) || 0)
        const failureInfo = getRetryFailureInfo(evaluateData)
        state.retryCount = retryCount
        state.attemptNo = MAX_RETRY_COUNT - retryCount + 1
        state.chanceCount = retryCount + 1
        state.failedItemName = failureInfo.name
        state.retryPhase = 'returning'
        retryHasLeftCenter = !arriveCenter
        flyingState.arriveCenter = 0
        clearPendingPrompts()
        proxy?.vknMessage.close()
        state.isShowMessage = false
        restoreRetryCheckpoint()
        resetEvaluateStatus()
        showMessage(`${failureInfo.promptText}，请飞至中心桶，开始第${state.attemptNo}次考试`, AlarmType.异常, 0, 'gjts','',true,failureInfo.code)
    }

    const handleRetryResume = () => {
        restoreRetryCheckpoint()
        resetEvaluateStatus()
        proxy?.vknMessage.close()
        state.isShowMessage = false
        state.retryPhase = 'running'
        retryHasLeftCenter = false
        state.failedItemName = ''
        flyingState.arriveCenter = 0
    }

    const hasFlowStep = (stepNameList:any, keyword:string) => {
        return Array.isArray(stepNameList) && stepNameList.some((step:string) => {
            return typeof step === 'string' && step.includes(keyword)
        })
    }

     //普通提示
    const normalPrompt = (options:any)=>{
        let {currentStepIndex, overLimit, stepNameList, arriveCenter, targetTime, isReady} = options || {};
        const hasUnlockStep = hasFlowStep(stepNameList, '解锁')
        const hasLandingStep = hasFlowStep(stepNameList, '降落')
        const canHandleActionStep = Array.isArray(stepNameList) && (currentStepIndex > 0 || !hasUnlockStep) && (hasUnlockStep || isReady)
        const flyToCenterPrompt = hasUnlockStep ? '请提升飞行高度至1.5米以上，并飞至中心桶悬停对尾' : '请保持1.5m以上高度，并飞至中心桶悬停对尾'
        if(hasUnlockStep && isReady && currentStepIndex == 0 && flyingState.currentMessage == MassageType.无){
            flyingState.currentMessage = MassageType.解锁;
            schedulePrompt(()=>{
                proxy?.vknMessage.close();
                showMessage($tips.OPEN_TIPS, AlarmType.操作, 0,'yjzldy','',true)
            }, 500)
        }
        if(canHandleActionStep && !state.isShowMessage && overLimit == 0){
            const canStartFirstAction = flyingState.currentMessage == MassageType.解锁 || (!hasUnlockStep && flyingState.currentMessage == MassageType.无)
            if(stepNameList[currentStepIndex] && /自旋/.test(stepNameList[currentStepIndex]) && canStartFirstAction){
                proxy?.vknMessage.close();
                flyingState.currentMessage = MassageType.自转;
                // showMessage("控制无人机到中心桶上方进行360°自旋。", 0, 'yjzldy',true)
                showMessage(flyToCenterPrompt, AlarmType.操作, 0, 'yjzldy','',true)
            }

            if(stepNameList[currentStepIndex] && /字/.test(stepNameList[currentStepIndex]) && (canStartFirstAction || flyingState.currentMessage == MassageType.自转)){
                proxy?.vknMessage.close();
                flyingState.currentMessage = MassageType.八字;
                let isHas360 = stepNameList.some((item:string) => {
                    return /自旋/.test(item)
                })
                if(isHas360){
                    if(!arriveCenter){
                        operationPrompt.spinCompleted = true;
                        showMessage(`${stepNameList[currentStepIndex - 1]}完成，请飞至中心桶`, AlarmType.操作, 5000, 'yjzldy','', true)
                    }
                }else{
                    showMessage(flyToCenterPrompt, AlarmType.操作, 0, 'yjzldy','',true)
                }

            }

            if(hasLandingStep && stepNameList[currentStepIndex] && /降落/.test(stepNameList[currentStepIndex]) && (flyingState.currentMessage == MassageType.自转 || flyingState.currentMessage == MassageType.八字)){
                proxy?.vknMessage.close();
                flyingState.currentMessage = MassageType.降落;
                // showMessage("控制无人机降落到地面。", 5000, 'yjzldy',true)
                showMessage("飞行完成，请将无人机降落", AlarmType.操作, 5000, 'yjzldy','',true)

            }
            if(arriveCenter){
                proxy?.vknMessage.close();
                if(flyingState.currentMessage == MassageType.八字){
                    operationPrompt.delayTime = 5000
                    if(operationPrompt.spinCompleted){
                        operationPrompt.spinCompleted = false;
                        showMessage('已到达中心桶，请保持对尾', AlarmType.操作, 3000, 'yjzldy','',true)
                    }else{
                        // showMessage(`${stepNameList[currentStepIndex - 1]}完成，已到达中心桶，请保持对尾`, AlarmType.操作, 5000, 'yjzldy','',true)
                        showMessage(`已到达中心桶，请保持对尾`, AlarmType.操作, 5000, 'yjzldy','',true)
                    }
                }else{
                    showMessage('已到达中心桶，请保持对尾', AlarmType.操作, 3000, 'yjzldy','',true)
                }
                schedulePrompt(() => {
                    proxy?.vknMessage.close();
                    showMessage(`${stepNameList[currentStepIndex]}开始，倒计时${targetTime}秒`, AlarmType.操作, 5000, 'yjzldy','',true)
                    if(operationPrompt.delayTime == 5000){
                        operationPrompt.delayTime = 3000
                    }
                }, operationPrompt.delayTime);
            }
        }
    }

    //异常提示
    const abnormalPrompt = (data:any)=>{
        if(data?.collisionAlarm && !flyingState.evaluateStatus.collisionAlarm){
            flyingState.evaluateStatus.collisionAlarm = true;
            proxy?.vknMessage.close();
            controlExceedMessage('注意障碍物，谨慎飞行')
        }
        if(!data?.collisionAlarm && flyingState.evaluateStatus.collisionAlarm){
            proxy?.vknMessage.close();
            flyingState.evaluateStatus.collisionAlarm = false;
            flyingState.isShowAlarm = false;
        }
        if(!flyingState.arriveCenter || flyingState.currentMessage == MassageType.降落){
            return
        }
        if(flyingState.evaluateStatus.height || flyingState.evaluateStatus.position || flyingState.evaluateStatus.time || flyingState.evaluateStatus.angle
            || flyingState.evaluateStatus.angleMinSpeed || flyingState.evaluateStatus.angleMaxSpeed || flyingState.evaluateStatus.minSpeed || flyingState.evaluateStatus.maxSpeed
        ){
            return
        }
        const {evaluateData,yawAngleSpeed,horizonalSpeed,mapPosition,mapYaw} = data
        const {heightOffset, positionOffset, time, angleOffset, angleSpeedOffset, speedOffset} = evaluateData || {};
        if(heightOffset){
            if(!flyingState.evaluateStatus.height && heightOffset?.value > heightOffset?.maxValue){
                flyingState.evaluateStatus.height = true;
                proxy?.vknMessage.close();
                controlExceedMessage('高度偏差过大，考核不通过',heightOffset?.code)
                const pos = {
                    x:mapPosition.x,
                    y:mapPosition.y,
                    yaw:mapYaw
                };
                const color = colorMap["height"];
                // @ts-ignore
                state.circleDatas.push({pos, color})
            }
        }

        if(positionOffset){
            if(!flyingState.evaluateStatus.position && positionOffset?.value > positionOffset?.maxValue){
                flyingState.evaluateStatus.position = true;
                proxy?.vknMessage.close();
                controlExceedMessage('位置偏差过大，考核不通过',positionOffset?.code)
                const pos = {
                    x:mapPosition.x,
                    y:mapPosition.y,
                    yaw:mapYaw
                };
                const color = colorMap["position"];
                // @ts-ignore
                state.circleDatas.push({pos, color})
            }
        }
        if(time && Array.isArray(time) && time.length){
            let timeIndex = Number(flyingState.evaluateStatus.time);

            if (timeIndex < time.length) {
                let needAlarm = false;
                for (let index = timeIndex; index < time.length; index++) {
                    if (time[index]?.value > time[index]?.maxValue) {
                        needAlarm = true;
                        break;
                    }
                }
                if (needAlarm) {
                    flyingState.evaluateStatus.time++;
                    proxy?.vknMessage.close();
                    controlExceedMessage('飞行超时，考核不通过',time[0].code)
                    const pos = {
                        x:mapPosition.x,
                        y:mapPosition.y,
                        yaw:mapYaw
                    };
                    const color = colorMap["timeDeviation"];
                    // @ts-ignore
                    state.circleDatas.push({pos, color})
                }

            }
        }

        if(angleOffset){
            if(!flyingState.evaluateStatus.angle && angleOffset?.value > angleOffset?.maxValue){
                flyingState.evaluateStatus.angle = true;
                proxy?.vknMessage.close();
                controlExceedMessage('航向夹角过大，考核不通过',angleOffset?.code)
                const pos = {
                    x:mapPosition.x,
                    y:mapPosition.y,
                    yaw:mapYaw
                };
                const color = colorMap["headAngle"];
                // @ts-ignore
                state.circleDatas.push({pos, color})
            }

        }

        if(angleSpeedOffset && flyingState.currentMessage == MassageType.自转){
            if(!flyingState.evaluateStatus.angleMinSpeed && angleSpeedOffset?.underMin){
                flyingState.evaluateStatus.angleMinSpeed = true;
                proxy?.vknMessage.close();
                controlExceedMessage('角速度过小，考核不通过',angleSpeedOffset?.code)
                const pos = {
                    x:mapPosition.x,
                    y:mapPosition.y,
                    yaw:mapYaw
                };
                const color = colorMap["angle"];
                // @ts-ignore
                state.circleDatas.push({pos, color})
            }

            if(!flyingState.evaluateStatus.angleMaxSpeed && angleSpeedOffset?.value > angleSpeedOffset?.maxValue){
                flyingState.evaluateStatus.angleMaxSpeed = true;
                proxy?.vknMessage.close();
                controlExceedMessage('角速度过大，考核不通过',angleSpeedOffset?.code)
                const pos = {
                    x:mapPosition.x,
                    y:mapPosition.y,
                    yaw:mapYaw
                };
                const color = colorMap["angle"];
                // @ts-ignore
                state.circleDatas.push({pos, color})
            }

        }

        if(speedOffset && flyingState.currentMessage == MassageType.八字){
            if(!flyingState.evaluateStatus.minSpeed && speedOffset?.underMin){
                flyingState.evaluateStatus.minSpeed = true;
                proxy?.vknMessage.close();
                controlExceedMessage('速度过小，考核不通过',speedOffset?.code)
                const pos = {
                    x:mapPosition.x,
                    y:mapPosition.y,
                    yaw:mapYaw
                };
                const color = colorMap["speed"];
                // @ts-ignore
                state.circleDatas.push({pos, color})
            }
            if(!flyingState.evaluateStatus.maxSpeed && speedOffset?.value > speedOffset.maxValue){
                flyingState.evaluateStatus.maxSpeed = true;
                proxy?.vknMessage.close();
                controlExceedMessage('速度过大，考核不通过',speedOffset?.code)
                const pos = {
                    x:mapPosition.x,
                    y:mapPosition.y,
                    yaw:mapYaw
                };
                const color = colorMap["speed"];
                // @ts-ignore
                state.circleDatas.push({pos, color})
            }
        }
    }

    //成绩页面中的重新开始按钮
    async function handleRestart(isBeforeUnload?:false | true){
        window?.playClick()
        const hasUnlockStep = hasFlowStep(flyingState.uavStatusList, '解锁')
        let param = {
            sceneId:store.state.persistence.currentUavInfo.sceneInfo.id,
            uavSpecificationId:state.uavId,
            userId:store.state.persistence.userInfo.userId,
            windPower:store.state.persistence.userTrainSettingInfo.windPower,
            windDirection:getWindDirection(store.state.persistence.examWindDirection),
            stationId:state.stationdId,
        }
        const {code, data} = await createTraining(param);
        if(code != $enum.EnumResult.SUCCESS){
            // showMessage("创建新训练失败！")
            return;
        }
        const {trainingId} = data as any || {};
        store.commit('persistence/setCurrentUavInfo', {
            ...store.state.persistence.currentUavInfo,
            trainingId: trainingId
        });
        state.trainingId = trainingId;
        retryCheckpoint = null
        attemptRecorder.reset()
        retryCheckpointKey = ''
        retryHasLeftCenter = false
        pointList = []
        state.retryPhase = 'running'
        state.retryCount = MAX_RETRY_COUNT
        state.attemptNo = 1
        state.chanceCount = MAX_RETRY_COUNT + 1
        state.failedItemName = ''
        state.finish = false
        state.evaluate = false
        state.gradScore = 0
        state.gradeTotalTime = 0
        state.flyingInfo = {}
        state.circleDatas = []
        state.aiCoachSettlementData = null
        resetView({
            trainingId:state.trainingId,
            retryCount:MAX_RETRY_COUNT,
        })
        //4. 重置飞行状态页面的状态,
        Object.assign(flyingState,{
            evaluateStatus:{
                height:false,
                position:false,
                time:false,
                angle:false,
                angleMaxSpeed:false,
                angleMinSpeed:false,
                calculateAngleSpeed:false, //到达中心桶并且角速度第一次超过最小值时改为true
                maxSpeed:false,
                minSpeed:false,
                calculateMinSpeed:false, //到达中心桶并且最小速度第一次超过最小值时改为true
                collisionAlarm:false
            },
            totalSeconds: -1, // 飞行总时间 秒
            currentSeconds: 0, // 当前时间 秒
            uavStatusList: [],
            currentStepIndex:0,
            currentMessage:MassageType.无,
            flyingInfo:{},
            pointList:[], // 点
            arriveCenter:0,//是否到达中心
        })
        //5. 重置进度条
        if (timeBox.value) {
            timeBox.value.style.right = `0%`;
        }
        if (progressRef.value) {
            progressRef.value.style.width = `0%`;
        }
        setTimeout(() => {
            state.circleDatas = [];
            if(state.gradDrawer){
                state.gradDrawer.destroy();
                state.gradDrawer = null;
            }
        }, 300);
        if(!isBeforeUnload){
            state.currentPage = PageName.飞行;
            proxy?.vknMessage.close();
            flyDeviationInfoRef?.value?.resetCharts()
            if(state.permissionLevel == 1){
                // 重置曲线数据
                if(state.flyDeviationViewMode == $enum.FlyDeviationViewMode.曲线){
                    state.flyDeviationViewMode = $enum.FlyDeviationViewMode.卡片
                }
            }
            if(hasUnlockStep){
                flyingState.currentMessage = MassageType.解锁;
                schedulePrompt(() => {
                    showMessage($tips.OPEN_TIPS, AlarmType.操作, 0,'yjzldy')
                }, 1000);
            }
        }else{
            state.currentPage = PageName.加载中;
        }
    }

    const handlerResiz = ()=>{
        if(state.gradDrawer){
            state.gradDrawer.resizeHandler();
        }
        resizeEcharts()
    }

    function handleBackCallback(type?: string){
        state.backByExitSimulateModule = true;
        enterOrExitUavSimulateModule({enter:false});
    }

    function handleBeforeUnload(e: BeforeUnloadEvent) {
        sessionStorage.removeItem('trainState')
        proxy?.vknMessage.close();
        enterOrExitUavSimulateModule({enter:false})
        showOrHideEngineView(false)
        state.circleDatas = [];
        if(state.gradDrawer){
            state.gradDrawer.destroy();
        }
        store.commit('persistence/setCurrentUavInfo', {
            ...store.state.persistence.currentUavInfo,
            trainingId: null
        });
    }

    function handleLock(){
        if(state.lockStatus == $enum.SWITCH_STATUS.ON && !flyingState.onGround){
            return;
        }
        let lock = -1;
        if(state.lockStatus == $enum.SWITCH_STATUS.OFF){
            lock = 1;
        }else{
            lock = 0;
        }
        window?.playClick()
        switchUnlockUav({unlock:lock})
    }

    function handleVisibilityChange(){
        if(document.visibilityState == "hidden"){
            state.pageVisible = false;
            state.canSetFps = false;
        }else{
            setTimeout(() => {
                state.pageVisible = true;
            }, 300);
            setTimeout(() => {
                state.canSetFps = true;
            }, 1500);
        }
    }
    function handleKeyDown(event:any)
    {
        if (state.currentPage == PageName.成绩) {
            if (event.key == "Escape") {
                handleBack('Escape');
            }
            return;
        }
        forceGainFocus({key: event.key});
    }

    //结束消息提示
    function handleFinishMessage(){
        if(state.currentInfoMessage){
            recordProcessMessage({message: null, messageType: 'info'});
            state.currentInfoMessage = null;
        }
        if(state.currentErrorMessage){
            recordProcessMessage({message: null, messageType: 'error', alarmCode:state.currentErrorAlarmCode});
            state.currentErrorMessage = null;
            state.currentErrorAlarmCode = null;
        }
    }

    function openFlyReplay(){
        window?.playClick()
        const deviationInfo = Object.keys(state.flyingInfo).filter(key => key !== 'timeDeviation').reduce((obj: any, key) => {obj[key] = state.flyingInfo[key].name;return obj;}, {})
        store.commit('viewData/setFlyReplayData', {
            title: state.loading3D.title,
            score: state.gradScore,
            isPass: state.evaluate,
            rating: state.aiCoachSettlementData?.rating,
            abilityData: state.aiCoachSettlementData?.radar,
            flightSummary: state.aiCoachSettlementData?.flightConclusion,
            trainingSuggestion: state.aiCoachSettlementData?.trainingSuggestion,
            windDirection: state.windDirection,
            windSpeed: -1,
            pageMode: 2,
            enterMode: 1,
            trainingId: state.trainingId,
            deviationInfo,
            enterPageMode: 1,
        })
        store.commit('viewData/setShowFlyReplay', true)
    }

    // 监听 store 中 flyReplay 关闭事件（从 App.vue 的 close 按钮触发）
    watch(() => store.getters['viewData/showFlyReplay'], (newVal, oldVal) => {
        if (oldVal === true && newVal === false) {
            resetView()
        }
    })

    ///////////////////////////////////////////////////////  生命周期
    onBeforeMount(() => {})
    onMounted(async () => {
        await nextTick();
        window.addEventListener('resize', handlerResiz)
        window.addEventListener('beforeunload', handleBeforeUnload)
        document.addEventListener('visibilitychange', handleVisibilityChange);
        document.addEventListener('keydown', handleKeyDown);
    })

    onBeforeUnmount(() => {
        isUnmounted = true
        clearPendingPrompts()
        handleFinishMessage()
        if(!state.backByExitSimulateModule){
            enterOrExitUavSimulateModule({enter:false})
        }
        showOrHideEngineView(false)
        if(state.isShowMessage){
            state.isShowMessage = false;
            proxy?.vknMessage.close();
        }
        state.circleDatas = [];
        if(state.gradDrawer){
            state.gradDrawer.destroy();
        }
        proxy?.vknMessage.close();
        store.commit("viewData/setEnterScene", false);
        window.removeEventListener('resize', handlerResiz);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        document.removeEventListener('keydown', handleKeyDown);
        if(store.getters['viewData/showFlyReplay']){
            store.commit('viewData/setShowFlyReplay', false)
        }
    })

    ///////////////////////////////////////////// 测试

    return {
        ...toRefs(state),
        ...toRefs(loadingState),
        ...toRefs(flyingState),
        $enum,
        connectIcon,
        currentTime,
        engineInitialized,
        lockIcon,
        gradTotalTime,
        loadingProgess,
        isOnlineSystem,
        handleBack,
        handleBackCallback,
        handleRestart,
        handleLock,
        handleOpenPDF,
        handleOpenSettingFullscreen,
        openFlyReplay,
    }
}
